import { useEffect, useRef } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { userInitService } from '../services/userInitService';

/**
 * Hook to automatically initialize new users with free tier subscription
 * Runs once when user signs in for the first time
 */
export const useUserInit = () => {
  const { userId, isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Only run if:
    // 1. Clerk is loaded
    // 2. User is signed in
    // 3. We have a userId
    // 4. Haven't already initialized
    if (!isLoaded || !isSignedIn || !userId || hasInitializedRef.current) {
      return;
    }

    const initializeUser = async () => {
      try {
        console.log('🔍 Checking if user needs initialization:', userId);
        
        // Check if user already has a subscription
        const needsInit = await userInitService.needsInitialization(userId);
        
        if (needsInit) {
          console.log('🆕 Initializing new user:', userId);
          
          const result = await userInitService.initializeNewUser(
            userId,
            user?.primaryEmailAddress?.emailAddress
          );
          
          if (result.success) {
            console.log('✅ User initialized successfully');
          } else {
            console.error('❌ Failed to initialize user:', result.error);
          }
        } else {
          console.log('ℹ️ User already initialized');
        }
        
        // Mark as initialized to prevent duplicate calls
        hasInitializedRef.current = true;
      } catch (error) {
        console.error('❌ Exception during user initialization:', error);
      }
    };

    initializeUser();
  }, [isLoaded, isSignedIn, userId, user]);
};

